import { FiTaskV2AssigneesHelper } from '../../fi-task-v2-assignees-helper';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { ObjectTypes } from '@fi-shared/types';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { Subject } from 'rxjs';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { FiTaskAssignmentType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';

@Component({
    selector: 'fi-task-v2-assignee-short',
    templateUrl: './fi-task-v2-assignee-short.component.html',
    styleUrls: ['./fi-task-v2-assignee-short.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-assignee-short' },
    standalone: false,
})
export class FiTaskV2AssigneeShortComponent implements OnInit, OnDestroy {
    @Input({ required: true }) taskId: number;
    @Input({ required: true }) taskCreatedOn: string;
    @Input({ required: true }) assignedUsers: IUserProfile[] = [];
    @Input({ required: true }) assignmentType: FiTaskAssignmentType;
    @Input({ required: true }) commentsCount: number = 0;
    @Input({ required: true }) accessRightFilterCollection: IAccessRightFilterCollection;
    @Input({ required: true }) accessToPreferredBusinessUnitMode: boolean;

    protected taskV2AssigneesHelper: FiTaskV2AssigneesHelper;

    protected readonly maxAssigneeUsersToShow = 6;
    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly ObjectTypes = ObjectTypes;

    constructor(
        private appTextService: AppTextService,
        private sharedActionsService: SharedActionsBaseService,
        private fiTaskSyncService: FiTaskSyncService,
        private fiFormatDateService: FiFormatDateService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.setAssigneesHelper();
        this.subscribeToSyncTasks();
    }

    private setAssigneesHelper() {
        this.taskV2AssigneesHelper = new FiTaskV2AssigneesHelper(
            this.appTextService,
            this.accessRightFilterCollection,
            this.assignedUsers,
            this.assignmentType,
        );
    }

    private subscribeToSyncTasks() {
        this.fiTaskSyncService.subscribeToTaskUpdatedBySync(this.taskId, this.ngOnDestroy$, (updatedTask) => {
            this.assignedUsers = updatedTask.AssignedUsers;
            this.accessRightFilterCollection = updatedTask.AccessRightFilterCollection;
            this.setAssigneesHelper();
            this.cdr.markForCheck();
        });
    }

    protected getAssignedToLabel(): string {
        return this.appTextService.get('task.assigned_to') + ':';
    }

    protected openUserDetails($event: MouseEvent, user: IUserProfile): void {
        $event.stopPropagation();
        this.sharedActionsService.openUserViewPopup(user.Id);
    }

    protected openUnitDetails($event: MouseEvent, unitId: number): void {
        $event.stopPropagation();
        this.sharedActionsService.openUnitViewPopup(unitId);
    }

    protected getCommentsCountLabel(): string {
        const commentLabel: string =
            this.commentsCount === 1
                ? this.appTextService.get('common.comment')
                : this.appTextService.get('common.comments');
        return `${this.commentsCount} ${commentLabel}`;
    }

    protected getCreatedLabel() {
        return this.fiTaskV2HelperService.getCreatedLabel(this.taskCreatedOn);
    }

    private ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
